import type { Turn } from './api';
import type { StoredLayer } from './timeline';
import { mmss } from './timeline';
import { unmark } from './prompter';

/**
 * The run of show (P10): what a host, a floor manager or a guest holds during
 * a recording — every scene with its time, who speaks, how it is framed,
 * the line, what is on screen and whether it is filmed. Markdown, so it
 * prints, pastes into a doc and reads in a text message. Pure over the card.
 */
export interface RunOfShowInput {
  title: string;
  speakers: string[];
  rundown: Turn[];
  lineSeconds?: number[];
  measured?: boolean;
  layers: StoredLayer[];
  /** A take's name by source id, for the scenes that cut to one. */
  takeNames?: Record<string, string>;
}

const FRAMING: Record<string, string> = {
  single: 'single', 'two-shot': 'two-shot', wide: 'wide', 'screen-share': 'screen share',
  insert: 'footage + face', boxed: 'footage in a box', graphic: 'host + graphic',
};

export function runOfShow(input: RunOfShowInput): string {
  const secs = input.lineSeconds ?? [];
  const total = secs.reduce((a, b) => a + (b || 0), 0);
  const lines: string[] = [
    `# ${input.title}`,
    '',
    `Cast: ${input.speakers.join(', ') || '—'}`,
    `Runs ${mmss(total)}${input.measured ? '' : ' (estimated)'} · ${input.rundown.length} scenes`,
    '',
    '| # | At | Who | Framing | Line | On screen |',
    '|---|----|-----|---------|------|-----------|',
  ];
  let at = 0;
  input.rundown.forEach((t, i) => {
    const onScreen = input.layers
      .filter((l) => l.anchor_id === t.id && l.props)
      .map((l) => (l.track === 'image' ? 'picture' : `${l.props?.style ?? 'banner'}: ${l.props?.text ?? ''}${l.props?.sub ? ` / ${l.props.sub}` : ''}`))
      .join('; ');
    const who = t.kind === 'title' ? 'title' : t.speaker;
    const framing = t.kind === 'title' ? 'held' : t.sourceId
      ? `cut: ${input.takeNames?.[t.sourceId] ?? 'footage'}`
      : FRAMING[t.shot ?? 'single'] ?? t.shot ?? '';
    const line = t.kind === 'title' ? `**${unmark(t.text)}**${t.sub ? ` — ${t.sub}` : ''}` : unmark(t.text);
    lines.push(`| ${i + 1} | ${mmss(at)} | ${cell(who)} | ${cell(framing)} | ${cell(line)} | ${cell(onScreen)} |`);
    at += secs[i] ?? 0;
  });
  const toFilm = input.rundown.filter((t) => t.kind !== 'title' && !t.sourceId && t.text.trim());
  if (toFilm.length) {
    lines.push('', `## To film (${toFilm.length})`, '');
    input.rundown.forEach((t, i) => { if (toFilm.includes(t)) lines.push(`- Scene ${i + 1} · ${t.speaker}: ${unmark(t.text)}`); });
  }
  lines.push('');
  return lines.join('\n');
}

function cell(s: string): string { return s.replace(/\|/g, '\\|').replace(/\s+/g, ' ').trim(); }
